/**
 * @description
 * Custom React hook for managing running deployment data from process-manager.
 * Provides real-time status of locally running VibeSDK deployments.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-11
 * @see admin/src/frontend/hooks/useRunningDeployments.test.ts
 */

import { useState, useEffect, useCallback } from 'react';

interface RunningDeployment {
  clientId: string;
  subdomain: string;
  status: 'starting' | 'running' | 'stopped' | 'crashed';
  pid: number | null;
  port: number | null;
  uptime: number;
  startedAt: string;
  health: 'healthy' | 'unhealthy';
}

interface UseRunningDeploymentsReturn {
  deployments: RunningDeployment[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for managing running deployments from process-manager
 */
export function useRunningDeployments(): UseRunningDeploymentsReturn {
  const [deployments, setDeployments] = useState<RunningDeployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch running deployments from admin API (queries all client databases)
   */
  const fetchDeployments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/deployments/status/all');
      
      if (!response.ok) {
        // Don't log 404s as errors - endpoint might not be implemented yet
        if (response.status === 404) {
          setDeployments([]);
          setLoading(false);
          return;
        }
        throw new Error(`Failed to fetch deployments: ${response.statusText}`);
      }
      
      const data = await response.json() as { deployments: RunningDeployment[] };
      setDeployments(data.deployments || []);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching running deployments:', err);
      // Set empty array on error so UI doesn't break
      setDeployments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch deployments on mount
  useEffect(() => {
    fetchDeployments();
    
    // Poll every 5 seconds for updates
    const interval = setInterval(fetchDeployments, 5000);
    
    return () => clearInterval(interval);
  }, [fetchDeployments]);

  return {
    deployments,
    loading,
    error,
    refetch: fetchDeployments,
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */